"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { getDatabase, ref, get, update } from "firebase/database";
import * as XLSX from "xlsx";
import { FaFileExcel, FaSync, FaUpload, FaCheck, FaTimes } from "react-icons/fa";
import "./userOrders.css";

const OrdersExcelSheet = () => {
  const [orders, setOrders] = useState([]);
  const [flatRows, setFlatRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);

  // Import / compare / apply
  const [importMode, setImportMode] = useState(false);
  const [changeMap, setChangeMap] = useState({}); // matchKey → { field → { from, to } }
  const [rowKeyMap, setRowKeyMap] = useState({}); // matchKey → original flat row
  const [isImporting, setIsImporting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [importError, setImportError] = useState("");
  const [changedRowCount, setChangedRowCount] = useState(0);

  // ──────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────

  const getSiteAddressString = (siteAddress) => {
    if (!siteAddress) return "";
    if (typeof siteAddress === "string") return siteAddress;
    return [siteAddress.address, siteAddress.state, siteAddress.pincode]
      .filter(Boolean)
      .join(", ");
  };

  const formatDateOnly = (value) => {
    if (value === null || value === undefined || value === "") return "";
    let date;
    if (typeof value === "number") {
      // Excel serial date (days since 1899-12-30) or JS timestamp
      if (value > 1000000000000) {
        date = new Date(value); // ms timestamp
      } else if (value > 20000 && value < 60000) {
        // Excel serial
        date = new Date((value - 25569) * 86400 * 1000);
      } else {
        date = new Date(value);
      }
    } else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(value);
    }
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  const toTimestamp = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") {
      if (value > 1000000000000) return value; // already ms
      if (value > 20000 && value < 60000) {
        // Excel serial
        return (value - 25569) * 86400 * 1000;
      }
      return value;
    }
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : 0;
  };

  /** Normalize any value to a comparable string (handles Excel number formatting). */
  const normalize = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "number") {
      // Avoid "10.0" vs "10" issues
      if (Number.isInteger(val)) return String(val);
      return String(val);
    }
    return String(val).trim();
  };

  /** True equality that treats 10, "10", "10.0", 10.00 as the same. */
  const valuesEqual = (a, b) => {
    const sa = normalize(a);
    const sb = normalize(b);
    if (sa === sb) return true;

    const na = Number(sa);
    const nb = Number(sb);
    if (Number.isFinite(na) && Number.isFinite(nb) && na === nb) return true;

    return false;
  };

  // ──────────────────────────────────────────────
  // Flatten order → one row per dispatch
  // ──────────────────────────────────────────────

  const flattenOrder = (order) => {
    const base = {
      "Order Date": formatDateOnly(order.timestamp),
      "Company Name": order.companyName || "",
      Brand: order.brand || "",
      Grade: order.grade || "",
      Code: order.code || "",
      "Order Quantity": order.quantity ?? "",
      "Quantity Type": order.quantityType || "bags",
      "Site Address": getSiteAddressString(order.siteAddress),
      "Transportation Type": order.siteAddress?.transportation_type || "",
      "Payment Amount": order.paymentAmount ?? "",
      Status: order.status || "Pending",
      Rate: order.rate ?? "",
      "Current Balance": order.currentBalance ?? 0,
      "Total Dispatched": order.totalDispatched ?? 0,
      "Total Pending": order.totalPending ?? order.quantity ?? 0,
      // Internal (also exported for stable matching)
      "User ID": order.userId || "",
      "Order ID": order.orderId || "",
      "Transaction Key": "",
      _orderId: order.orderId,
      _userId: order.userId,
      _timestamp: order.timestamp,
    };

    const txEntries = order.dispatchTransactions
      ? Object.entries(order.dispatchTransactions)
      : [];

    if (txEntries.length === 0) {
      return [
        {
          ...base,
          "Dispatch Date": formatDateOnly(order.dispatchDate),
          "Dispatch Qty": "",
          "Shipping Detail (Truck/Driver)": order.shippingDetail || "",
          "Transaction Key": "",
          _txKey: null,
        },
      ];
    }

    return txEntries
      .map(([txKey, tx]) => ({ txKey, tx }))
      .sort(
        (a, b) => toTimestamp(b.tx.timestamp) - toTimestamp(a.tx.timestamp)
      )
      .map(({ txKey, tx }) => ({
        ...base,
        "Dispatch Date":
          formatDateOnly(tx.timestamp) || formatDateOnly(order.dispatchDate),
        "Dispatch Qty": tx.dispatch ?? "",
        "Shipping Detail (Truck/Driver)": tx.shippingDetail || "",
        "Transaction Key": txKey,
        _txKey: txKey,
        _txTimestamp: tx.timestamp,
      }));
  };

  // ──────────────────────────────────────────────
  // Fetch
  // ──────────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const db = getDatabase();
      const [ordersSnapshot, sitesSnapshot] = await Promise.all([
        get(ref(db, "orders")),
        get(ref(db, "new-code")),
      ]);

      if (!ordersSnapshot.exists()) {
        setOrders([]);
        setFlatRows([]);
        setCompanies([]);
        setError("No orders found.");
        setIsLoading(false);
        return;
      }

      const data = ordersSnapshot.val();
      const sitesData = sitesSnapshot.exists() ? sitesSnapshot.val() : {};
      const filtered = [];
      const companySet = new Set();
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

      Object.keys(data).forEach((uid) => {
        const userOrders = data[uid];
        if (!userOrders || typeof userOrders !== "object") return;

        Object.keys(userOrders).forEach((orderId) => {
          const order = userOrders[orderId];
          if (!order || typeof order !== "object") return;

          const isCementOrder =
            !order.category ||
            order.category === "Cement" ||
            order.category === "Bulker Cement";
          if (!isCementOrder) return;

          const getLastTxnTs = (txns) => {
            if (!txns) return null;
            const arr = Object.values(txns);
            arr.sort(
              (a, b) => toTimestamp(b.timestamp) - toTimestamp(a.timestamp)
            );
            return arr[0]?.timestamp || null;
          };

          const lastTs = getLastTxnTs(order.transactions);
          if (order.completed && lastTs && toTimestamp(lastTs) < oneWeekAgo) {
            return;
          }

          let siteBalance = 0;
          if (order.code && sitesData[uid]?.codes) {
            Object.values(sitesData[uid].codes).forEach((codeRecord) => {
              if (
                codeRecord?.codeID?.trim().toLowerCase() ===
                String(order.code).trim().toLowerCase()
              ) {
                siteBalance = codeRecord.balance ?? 0;
              }
            });
          } else if (
            order.siteAddress &&
            sitesData[uid]?.brands?.[order.brand]?.sites
          ) {
            Object.values(sitesData[uid].brands[order.brand].sites).forEach(
              (site) => {
                if (site?.address === order.siteAddress?.address) {
                  siteBalance = site.balance ?? 0;
                }
              }
            );
          }

          const quantity =
            order.quantityData?.total ?? order.quantity ?? 0;
          let totalDispatched = 0;
          let totalPending = quantity;

          if (order.dispatchTransactions) {
            const txns = Object.values(order.dispatchTransactions);
            totalDispatched = txns.reduce(
              (sum, tx) => sum + (Number(tx.dispatch) || 0),
              0
            );
            const sortedTxns = [...txns].sort(
              (a, b) => toTimestamp(b.timestamp) - toTimestamp(a.timestamp)
            );
            const latestTransaction = sortedTxns[0];
            totalPending =
              latestTransaction?.pending != null
                ? Number(latestTransaction.pending)
                : Math.max(0, quantity - totalDispatched);
          }

          filtered.push({
            orderId,
            userId: uid,
            ...order,
            quantity,
            quantityType: order.quantityType || "bags",
            currentBalance: siteBalance,
            totalDispatched,
            totalPending,
          });

          if (order.companyName) companySet.add(order.companyName);
        });
      });

      filtered.sort(
        (a, b) => toTimestamp(b.timestamp) - toTimestamp(a.timestamp)
      );

      setOrders(filtered);
      setCompanies(Array.from(companySet).sort());
      setIsLoading(false);
    } catch (err) {
      setError("Error fetching orders: " + err.message);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const rows = [];
    orders.forEach((order) => {
      rows.push(...flattenOrder(order));
    });
    setFlatRows(rows);
  }, [orders]);

  // ──────────────────────────────────────────────
  // Columns
  // ──────────────────────────────────────────────

  // Visible columns in the HTML table
  const columns = [
    "Order Date",
    "Company Name",
    "Brand",
    "Grade",
    "Code",
    "Order Quantity",
    "Quantity Type",
    "Site Address",
    "Transportation Type",
    "Payment Amount",
    "Status",
    "Rate",
    "Dispatch Date",
    "Dispatch Qty",
    "Total Dispatched",
    "Total Pending",
    "Shipping Detail (Truck/Driver)",
    "Current Balance",
  ];

  // Extra columns written only to Excel (for stable matching)
  const idColumns = ["User ID", "Order ID", "Transaction Key"];

  // All columns that go into the Excel file
  const exportColumns = [...columns, ...idColumns];

  // Every field the user is allowed to change
  const updatableFields = [
    "Order Date",
    "Company Name",
    "Brand",
    "Grade",
    "Code",
    "Order Quantity",
    "Quantity Type",
    "Site Address",
    "Transportation Type",
    "Payment Amount",
    "Status",
    "Rate",
    "Dispatch Date",
    "Dispatch Qty",
    "Total Dispatched",
    "Total Pending",
    "Shipping Detail (Truck/Driver)",
    // Current Balance is derived from sites – we still allow it if you want,
    // but it is not written back to the order document (see applyChanges).
  ];

  /**
   * Preferred stable key (uses the ID columns we export).
   * Falls back to the old composite key for older Excel files that lack IDs.
   */
  const makeMatchKey = (row) => {
    const uid = normalize(row["User ID"] ?? row._userId);
    const oid = normalize(row["Order ID"] ?? row._orderId);
    const txk = normalize(row["Transaction Key"] ?? row._txKey ?? "");

    if (uid && oid) {
      return `${uid}|${oid}|${txk}`;
    }

    // Fallback for files exported before this change
    return [
      normalize(row["Company Name"]),
      normalize(row["Code"]),
      normalize(row["Order Date"]),
      normalize(row.Brand),
      normalize(row.Grade),
      normalize(row["Dispatch Date"]),
    ].join("|");
  };

  // ──────────────────────────────────────────────
  // Display rows (with optional change overlay)
  // ──────────────────────────────────────────────

  const { displayRows, filteredChangeKeys } = useMemo(() => {
    let base = [...flatRows];

    if (selectedCompany) {
      base = base.filter((row) => row["Company Name"] === selectedCompany);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      base = base.filter((row) =>
        Object.values(row).some(
          (value) =>
            value != null && String(value).toLowerCase().includes(query)
        )
      );
    }

    if (!importMode || Object.keys(changeMap).length === 0) {
      return { displayRows: base, filteredChangeKeys: new Set() };
    }

    const keysWithChanges = new Set();
    const overlaid = base.map((row) => {
      const key = makeMatchKey(row);
      const changes = changeMap[key];
      if (!changes) return row;

      keysWithChanges.add(key);
      const next = { ...row };
      Object.keys(changes).forEach((field) => {
        next[field] = changes[field].to;
        next[`__changed__${field}`] = true;
      });
      return next;
    });

    return { displayRows: overlaid, filteredChangeKeys: keysWithChanges };
  }, [flatRows, selectedCompany, searchQuery, importMode, changeMap]);

  // ──────────────────────────────────────────────
  // Export
  // ──────────────────────────────────────────────

  const exportToExcel = () => {
    if (displayRows.length === 0) {
      alert("No data to export.");
      return;
    }

    const worksheetData = [
      exportColumns,
      ...displayRows.map((row) =>
        exportColumns.map((column) => {
          // Prefer the internal ID fields when present
          if (column === "User ID") return row._userId ?? row["User ID"] ?? "";
          if (column === "Order ID") return row._orderId ?? row["Order ID"] ?? "";
          if (column === "Transaction Key")
            return row._txKey ?? row["Transaction Key"] ?? "";
          return row[column] ?? "";
        })
      ),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Make ID columns a bit narrower and put them at the end
    worksheet["!cols"] = exportColumns.map((column) => ({
      wch:
        Math.max(
          column.length,
          ...displayRows
            .slice(0, 40)
            .map((row) => {
              if (column === "User ID") return String(row._userId ?? "").length;
              if (column === "Order ID") return String(row._orderId ?? "").length;
              if (column === "Transaction Key")
                return String(row._txKey ?? "").length;
              return String(row[column] ?? "").length;
            })
        ) + 2,
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Cement Orders");
    const fileName = `All_Cement_Orders_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // ──────────────────────────────────────────────
  // Import
  // ──────────────────────────────────────────────

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportError("");
    setSuccessMessage("");
    setIsImporting(true);
    setImportMode(false);
    setChangeMap({});
    setRowKeyMap({});
    setChangedRowCount(0);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          raw: false, // get formatted strings (important for dates & numbers)
        });

        if (!json || json.length === 0) {
          setImportError("Imported file has no data rows.");
          setIsImporting(false);
          return;
        }

        // Map every known column (case-insensitive header match)
        const normalizedImported = json.map((rawRow) => {
          const mapped = {};
          [...exportColumns].forEach((col) => {
            let found = rawRow[col];
            if (found === undefined) {
              const key = Object.keys(rawRow).find(
                (k) => k.trim().toLowerCase() === col.toLowerCase()
              );
              found = key != null ? rawRow[key] : "";
            }
            mapped[col] = found ?? "";
          });
          return mapped;
        });

        // Build lookup from current data using the same match key
        const currentByKey = new Map();
        const keyToOriginal = {};
        flatRows.forEach((row) => {
          const key = makeMatchKey(row);
          if (!currentByKey.has(key)) {
            currentByKey.set(key, row);
            keyToOriginal[key] = row;
          }
        });

        const newChangeMap = {};
        let changedCount = 0;

        normalizedImported.forEach((impRow) => {
          const key = makeMatchKey(impRow);
          const orig = currentByKey.get(key);
          if (!orig) return; // row not found in current data → skip (no create yet)

          const fieldChanges = {};
          updatableFields.forEach((field) => {
            const a = orig[field];
            const b = impRow[field];
            if (!valuesEqual(a, b)) {
              fieldChanges[field] = {
                from: a ?? "",
                to: b ?? "",
              };
            }
          });

          if (Object.keys(fieldChanges).length > 0) {
            newChangeMap[key] = fieldChanges;
            changedCount += 1;
          }
        });

        setChangeMap(newChangeMap);
        setRowKeyMap(keyToOriginal);
        setChangedRowCount(changedCount);
        setImportMode(true);
        setIsImporting(false);

        if (changedCount === 0) {
          setSuccessMessage(
            "Import completed. No differences found on matching rows."
          );
        } else {
          setSuccessMessage(
            `Import ready: ${changedCount} row(s) have changes (red text). Review then click Apply.`
          );
        }
      } catch (err) {
        console.error(err);
        setImportError(
          "Failed to read Excel file: " + (err.message || "Unknown error")
        );
        setIsImporting(false);
      }
    };

    reader.onerror = () => {
      setImportError("Failed to read the selected file.");
      setIsImporting(false);
    };

    reader.readAsArrayBuffer(file);
    event.target.value = "";
  };

  // ──────────────────────────────────────────────
  // Apply changes to Firebase
  // ──────────────────────────────────────────────

  const applyChanges = async () => {
    const keys = Object.keys(changeMap);
    if (keys.length === 0) {
      setImportError("No changes to apply.");
      return;
    }

    const confirmed = window.confirm(
      `Update ${keys.length} order row(s) in Firebase?\n\nThis modifies live database records.`
    );
    if (!confirmed) return;

    setIsApplying(true);
    setImportError("");
    setSuccessMessage("");

    try {
      const db = getDatabase();
      const updates = {};
      let pathsWritten = 0;

      keys.forEach((key) => {
        const orig = rowKeyMap[key];
        const changes = changeMap[key];
        if (!orig?._userId || !orig?._orderId || !changes) {
          console.warn("Skip apply — missing ids for key", key, orig);
          return;
        }

        const basePath = `orders/${orig._userId}/${orig._orderId}`;

        // Order-level field mapping
        const orderFieldMap = {
          Status: "status",
          Rate: "rate",
          "Payment Amount": "paymentAmount",
          "Order Quantity": "quantity",
          "Quantity Type": "quantityType",
          "Total Dispatched": "totalDispatched",
          "Total Pending": "totalPending",
          "Company Name": "companyName",
          Brand: "brand",
          Grade: "grade",
          Code: "code",
        };

        Object.entries(changes).forEach(([field, { to }]) => {
          // ── Order-level simple fields ──
          if (orderFieldMap[field]) {
            const fbField = orderFieldMap[field];
            let value = to;

            if (
              [
                "rate",
                "paymentAmount",
                "quantity",
                "totalDispatched",
                "totalPending",
              ].includes(fbField)
            ) {
              const num = Number(value);
              if (Number.isFinite(num) && String(value).trim() !== "") {
                value = num;
              }
            }

            updates[`${basePath}/${fbField}`] = value;
            pathsWritten += 1;
          }

          // ── Order Date → timestamp ──
          if (field === "Order Date") {
            const ts = toTimestamp(to);
            if (ts) {
              updates[`${basePath}/timestamp`] = ts;
              pathsWritten += 1;
            }
          }

          // ── Dispatch Date ──
          if (field === "Dispatch Date") {
            const ts = toTimestamp(to);
            if (ts) {
              if (orig._txKey) {
                updates[
                  `${basePath}/dispatchTransactions/${orig._txKey}/timestamp`
                ] = ts;
              } else {
                updates[`${basePath}/dispatchDate`] = ts;
              }
              pathsWritten += 1;
            }
          }

          // ── Dispatch Qty / Shipping Detail ──
          if (
            field === "Dispatch Qty" ||
            field === "Shipping Detail (Truck/Driver)"
          ) {
            if (orig._txKey) {
              const txPath = `${basePath}/dispatchTransactions/${orig._txKey}`;
              if (field === "Dispatch Qty") {
                const num = Number(to);
                updates[`${txPath}/dispatch`] =
                  Number.isFinite(num) && String(to).trim() !== "" ? num : to;
                pathsWritten += 1;
              }
              if (field === "Shipping Detail (Truck/Driver)") {
                updates[`${txPath}/shippingDetail`] = to;
                pathsWritten += 1;
              }
            } else {
              // No transaction node yet – write shipping on the order itself
              if (field === "Shipping Detail (Truck/Driver)") {
                updates[`${basePath}/shippingDetail`] = to;
                pathsWritten += 1;
              }
              // Creating a new dispatch transaction on the fly is possible but
              // left out for safety (would need a new key generation strategy).
            }
          }

          // ── Transportation Type (lives under siteAddress) ──
          if (field === "Transportation Type") {
            updates[`${basePath}/siteAddress/transportation_type`] = to;
            pathsWritten += 1;
          }

          // ── Site Address (best-effort) ──
          if (field === "Site Address") {
            // We store the free-text version. If the original was an object
            // we still overwrite the whole node with a string for simplicity.
            // Adjust if you need to keep the object shape.
            updates[`${basePath}/siteAddress`] = to;
            pathsWritten += 1;
          }
        });
      });

      if (Object.keys(updates).length === 0) {
        setImportError(
          "No valid Firebase paths built. Check that rows have User ID / Order ID and Transaction Key."
        );
        setIsApplying(false);
        return;
      }

      console.log("Firebase updates to apply:", updates);

      await update(ref(db), updates);

      setSuccessMessage(
        `Saved ${keys.length} row(s) / ${pathsWritten} field(s) to Firebase. Refreshing…`
      );
      setImportMode(false);
      setChangeMap({});
      setRowKeyMap({});
      setChangedRowCount(0);

      await fetchOrders();
      setSuccessMessage(
        `Successfully applied ${keys.length} change(s) to the database.`
      );
    } catch (err) {
      console.error("Apply failed:", err);
      setImportError(
        "Failed to apply changes: " + (err.message || "Unknown error")
      );
    } finally {
      setIsApplying(false);
    }
  };

  const cancelImport = () => {
    setImportMode(false);
    setChangeMap({});
    setRowKeyMap({});
    setChangedRowCount(0);
    setImportError("");
    setSuccessMessage("Import cancelled. Showing original data.");
  };

  // ──────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <p>Loading all orders…</p>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="orders-container">
      <h1>
        All Cement Orders – Excel Sheet
        <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 12 }}>
          (one row per dispatch / truck • all brands)
        </span>
      </h1>

      {successMessage && (
        <div
          style={{
            background: "#e6f7e6",
            border: "1px solid #4caf50",
            color: "#2e7d32",
            padding: "10px 14px",
            borderRadius: 6,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FaCheck /> {successMessage}
          <button
            type="button"
            onClick={() => setSuccessMessage("")}
            style={{
              marginLeft: "auto",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#2e7d32",
            }}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {importError && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #f44336",
            color: "#c62828",
            padding: "10px 14px",
            borderRadius: 6,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {importError}
          <button
            type="button"
            onClick={() => setImportError("")}
            style={{
              marginLeft: "auto",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#c62828",
            }}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {importMode && changedRowCount > 0 && (
        <div
          style={{
            background: "#fff8e1",
            border: "1px solid #ffb300",
            color: "#e65100",
            padding: "10px 14px",
            borderRadius: 6,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span>
            <strong>{changedRowCount}</strong> row(s) have changes. Changed
            cells are in <strong style={{ color: "#c62828" }}>red</strong>.
            Click Apply to write them to Firebase.
          </span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              type="button"
              className="excel-import-button"
              onClick={cancelImport}
              disabled={isApplying}
            >
              Cancel import
            </button>
            <button
              type="button"
              className="excel-import-button"
              onClick={applyChanges}
              disabled={isApplying}
              style={{
                background: "#2e7d32",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {isApplying ? (
                "Applying…"
              ) : (
                <>
                  <FaCheck /> Apply {changedRowCount} change(s)
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="orders-controls" style={{ marginBottom: 16 }}>
        <div className="control-item">
          <label>Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search any column…"
          />
        </div>

        <div className="control-item">
          <label>Company</label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
          >
            <option value="">All Companies</option>
            {companies.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>

        <div
          className="control-item"
          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          <button
            type="button"
            className="excel-import-button"
            onClick={exportToExcel}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <FaFileExcel /> Download Excel (.xlsx)
          </button>

          <label
            className="excel-import-button"
            style={{
              display: "flex",
              color:"white",
              fontSize:"14px",
              alignItems: "center",
              gap: 8,
              cursor: isImporting ? "wait" : "pointer",
              opacity: isImporting ? 0.7 : 1,
            }}
          >
            <FaUpload />
            {isImporting ? "Importing…" : "Import Excel"}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleImportFile}
              disabled={isImporting || isApplying}
              style={{ display: "none" }}
            />
          </label>
        </div>
      </div>

      <p style={{ marginBottom: 8, color: "#555" }}>
        Showing <strong>{displayRows.length}</strong> row(s)
        {selectedCompany || searchQuery ? " (filtered)" : ""} •{" "}
        <strong>All brands</strong>
        {importMode && changedRowCount > 0 && (
          <>
            {" "}
            •{" "}
            <span style={{ color: "#c62828", fontWeight: 600 }}>
              {filteredChangeKeys.size} changed row(s) visible
            </span>
          </>
        )}
      </p>

      <div className="orders-excel-grid-container">
        <table className="orders-excel-grid">
          <thead>
            <tr>
              {columns.map((heading) => (
                <th key={heading}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: 24 }}
                >
                  No rows match the current filters.
                </td>
              </tr>
            ) : (
              displayRows.map((row, index) => {
                const rowHasChange = columns.some(
                  (col) => row[`__changed__${col}`]
                );
                return (
                  <tr
                    key={`${row["Company Name"]}-${row._orderId}-${row._txKey}-${index}`}
                    style={
                      rowHasChange ? { background: "#fff8e1" } : undefined
                    }
                  >
                    {columns.map((column) => {
                      const isChanged = !!row[`__changed__${column}`];
                      const prev =
                        changeMap[makeMatchKey(row)]?.[column]?.from;
                      return (
                        <td
                          key={column}
                          title={
                            isChanged
                              ? `Changed from: ${prev ?? ""}`
                              : String(row[column] ?? "")
                          }
                          style={
                            isChanged
                              ? { color: "#c62828", fontWeight: 600 }
                              : undefined
                          }
                        >
                          {row[column] ?? ""}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrdersExcelSheet;